/* eslint-disable @typescript-eslint/no-explicit-any */
// ===== AUTH THUNKS =====
// src/features/auth/state/auth-state/authThunks.ts

import { createAsyncThunk } from '@reduxjs/toolkit';
import type { RootState } from '@/app/store';
import { authAPI } from '../../api-layer/clients/AuthAPIClient';
import type {
  AuthError,
  PasswordResetRequest,
  // PasswordUpdateRequest, // Removed because it does not exist
  EmailVerificationRequest,
  WorkspaceValidationResponse,
} from '../../types/auth.types';

// Define interface for trust device response
interface TrustDeviceResponse {
  deviceId: string;
  trustedUntil: string;
}

// Password reset flow
export const requestPasswordResetThunk = createAsyncThunk<
  { message: string },
  PasswordResetRequest,
  { rejectValue: AuthError }
>(
  'auth/requestPasswordReset',
  async (data, { rejectWithValue }) => {
    try {
      await authAPI.requestPasswordReset(data);
      return { message: 'Password reset request successful' };
    } catch (error: any) {
      return rejectWithValue({
        code: error.response?.data?.code || 'PASSWORD_RESET_FAILED',
        message: error.response?.data?.message || 'Failed to request password reset',
      });
    }
  }
);

export const resetPasswordThunk = createAsyncThunk<
  { message: string },
  { token: string; password: string },
  { rejectValue: AuthError }
>(
  'auth/resetPassword',
  async (data, { rejectWithValue }) => {
    try {
      await authAPI.resetPassword([data]);
      // Handle boolean response from resetPassword
      return { message: 'Password reset successful' };
    } catch (error: any) {
      return rejectWithValue({
        code: error.response?.data?.code || 'PASSWORD_RESET_FAILED',
        message: error.response?.data?.message || 'Failed to reset password',
      });
    }
  }
);

// Email verification
export const verifyEmailThunk = createAsyncThunk<
  { message: string },
  EmailVerificationRequest,
  { rejectValue: AuthError }
>(
  'auth/verifyEmail',
  async (data, { rejectWithValue }) => {
    try {
      await authAPI.verifyEmail(data);
      return { message: 'Email verified successfully' };
    } catch (error: any) {
      return rejectWithValue({
        code: error.response?.data?.code || 'EMAIL_VERIFICATION_FAILED',
        message: error.response?.data?.message || 'Failed to verify email',
      });
    }
  }
);

export const resendVerificationEmailThunk = createAsyncThunk<
  { message: string },
  void,
  { rejectValue: AuthError }
>(
  'auth/resendVerificationEmail',
  async (_, { getState, rejectWithValue }) => {
    try {
      const state = getState() as RootState;
      const email = state.auth.user?.email;
      
      if (!email) {
        return rejectWithValue({
          code: 'NO_EMAIL',
          message: 'No email address found',
        });
      }
      
      await authAPI.resendVerificationEmail({ email });
      return { message: 'Verification email resent successfully' };
    } catch (error: any) {
      return rejectWithValue({
        code: error.response?.data?.code || 'RESEND_FAILED',
        message: error.response?.data?.message || 'Failed to resend verification email',
      });
    }
  }
);

// Workspace validation
export const validateWorkspaceThunk = createAsyncThunk<
  WorkspaceValidationResponse,
  string,
  { rejectValue: AuthError }
>(
  'auth/validateWorkspace',
  async (workspace, { rejectWithValue }) => {
    try {
      const response = await authAPI.validateWorkspace(workspace);
      return response;
    } catch (error: any) {
      return rejectWithValue({
        code: error.response?.data?.code || 'WORKSPACE_VALIDATION_FAILED',
        message: error.response?.data?.message || 'Failed to validate workspace',
      });
    }
  }
);

// Update password
export const updatePasswordThunk = createAsyncThunk<
  { message: string },
  { currentPassword: string; newPassword: string },
  { rejectValue: AuthError }
>(
  'auth/updatePassword',
  async (data, { rejectWithValue }) => {
    try {
      await authAPI.updatePassword(data.currentPassword, data.newPassword);
      return { message: 'Password updated successfully' };
    } catch (error: any) {
      return rejectWithValue({
        code: error.response?.data?.code || 'PASSWORD_UPDATE_FAILED',
        message: error.response?.data?.message || 'Failed to update password',
      });
    }
  }
);

export const trustDeviceThunk = createAsyncThunk<
  { deviceId: string; trustedUntil: string },
  { deviceFingerprint: string; trustDuration?: number },
  { rejectValue: AuthError }
>(
  'auth/trustDevice',
  async (data, { rejectWithValue }) => {
    try {
      const response = await authAPI.trustDevice(data) as TrustDeviceResponse;
      
      // Ensure response has the expected shape
      if (!response || typeof response !== 'object' || !('deviceId' in response) || !('trustedUntil' in response)) {
        return rejectWithValue({
          code: 'INVALID_RESPONSE',
          message: 'Invalid trust device response format',
        });
      }
      
      return {
        deviceId: response.deviceId,
        trustedUntil: response.trustedUntil
      };
    } catch (error: any) {
      return rejectWithValue({
        code: error.response?.data?.code || 'DEVICE_TRUST_FAILED',
        message: error.response?.data?.message || 'Failed to trust device',
      });
    }
  }
);
